// Outstanding Appeals — every unresolved appeal, ACROSS ALL YEARS, in one pull.
//
// This is the report the analyst compiles by hand today: one run per year in the source system,
// then merged. The whole point is that a 2022 appeal still open in 2026 appears alongside this
// year's, so there is NO year filter and no default year narrowing. Her national-client version
// reads "136 appeals pending totalling $1.54B — 3 in 2022, 7 in 2023, 48 in 2024, 78 in 2025";
// that split-by-year shape is what the summary reproduces.
//
// ⛔ OUTSTANDING IS A NAMED SET, not a literal — APPEAL_STATUS_OUTSTANDING in constants/statusCodes.
// 'Value Accepted' and 'Withdrawn' are terminal. 'Value Pending' means no notice has arrived, so
// there is nothing to appeal.
//
// ⛔ NULL STATUS IS EXCLUDED, and that needs saying out loud rather than silently. The year roll
// writes appeal_status null (`null::text -- appeal_status NOT carried`), so a rolled year with no
// notice would otherwise read as an open appeal. But a client whose whole portfolio is nulls would
// then see an empty report with no explanation — so the empty state names the reason. Note this is
// the one place the layer's usual "(None) is always selectable" rule is deliberately overridden:
// the exclusion is in buildRows, not in a filter, because "no status" is not an appeal state.
//
// Everything else — the fetch, the representation gate, the tax derivation — is the shared
// ../parcelYearRows scope, identical to Property Executive Summary.

import { PARCEL_YEAR_COLUMNS, fetchParcelYearScope, buildParcelYearRows, NOTICE_OPTIONS } from '../parcelYearRows';
import { APPEAL_STATUS_OUTSTANDING } from '../../constants/statusCodes';
import { toNumber } from '../format';
import { assertRowFields } from '../grouping';

const OUTSTANDING = new Set(APPEAL_STATUS_OUTSTANDING);

// Value under appeal = the proposed value at the HIGHEST level reached, on the taxable basis —
// what is actually in dispute right now, not what it started at. buildParcelYearRows already
// resolves the final level and puts its value in final_value; on an OPEN appeal no decision exists,
// so that is the deepest level opened, which is exactly the figure wanted here.
const valueUnderAppeal = (r) => toNumber(r.final_value) ?? toNumber(r.proposed_value);

// ⛔ `value` STAYS NULL when no row in the year carried one. The accumulator used `?? 0`, so a year
// of appeals with no values summed to 0 and the tile read "$0 under appeal" — an assertion, not an
// absence. Counting is different: an appeal with no value is still an appeal, so `count` is a real
// zero-based tally. Same class as the average-row bug; see known-debt §3b.
const byYear = (rows) => {
  const m = new Map();
  rows.forEach((r) => {
    const y = String(r.tax_year);
    const g = m.get(y) || { year: y, count: 0, value: null };
    g.count += 1;
    const v = valueUnderAppeal(r);
    if (v != null) g.value = (g.value ?? 0) + v;
    m.set(y, g);
  });
  return [...m.values()].sort((a, b) => a.year.localeCompare(b.year));
};

const compact = (n) => (n == null ? '—'
  : Math.abs(n) >= 1e9 ? `$${(n / 1e9).toFixed(2)}B`
  : Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(1)}M`
  : `$${Math.round(n).toLocaleString()}`);

export const outstandingAppeals = {
  id: 'outstanding_appeals',
  title: 'Outstanding Appeals',
  description: 'Every unresolved appeal across all years, with counts and value pending by year.',

  // ⛔ NO 'years' FILTER. Narrowing by year is the manual step this report exists to remove.
  filters: [
    'client',
    { key: 'basis', type: 'toggle', label: 'Basis', default: 'taxable',
      options: [{ value: 'taxable', label: 'Taxable' }, { value: 'market', label: 'Market' }] },
    { key: 'status', type: 'enum', label: 'Status', from: 'appeal_status' },
    { key: 'state', type: 'enum', label: 'State', from: 'state' },
    { key: 'property', type: 'enum', label: 'Property', from: 'property', picker: 'search', noun: 'properties' },
    ...NOTICE_OPTIONS,
  ],

  // 5c: the same detail columns as the executive summary.
  columns: PARCEL_YEAR_COLUMNS,

  // allYears is forced regardless of what the view passes — there is no year control to set it.
  fetch: (ctx) => fetchParcelYearScope({ ...ctx, years: null, allYears: true }, 'outstanding appeals'),

  buildRows(raw, ctx) {
    const all = buildParcelYearRows(raw, ctx);
    assertRowFields(all, ['tax_year', 'appeal_status', 'final_value', 'proposed_value'], 'outstanding appeals');
    const open = all.filter((r) => OUTSTANDING.has(r.appeal_status));
    if (!open.length && all.length) {
      const nulls = all.filter((r) => r.appeal_status == null).length;
      console.info(
        `[outstanding appeals] no open appeals in ${all.length} account-year(s)` +
        (nulls ? ` — ${nulls} carry NO appeal status (rolled years write it null), which is not an open appeal` : '')
      );
    }
    return open;
  },

  // 5d — counts and value pending BY YEAR, plus a grand total. Rendered as tiles, one per year.
  // Runs on the FILTERED rows the table shows (ReportView passes them), per 3b.
  summary(rows) {
    const groups = byYear(rows);
    const total = groups.reduce((s, g) => ({
      count: s.count + g.count,
      value: g.value == null ? s.value : (s.value ?? 0) + g.value,
    }), { count: 0, value: null });
    return [
      ...groups.map((g) => ({ label: g.year, value: String(g.count), sub: compact(g.value) })),
      { label: 'Total', value: String(total.count), sub: compact(total.value) },
    ];
  },

  // 5e — a two-tab workbook: Summary then Detail. Uses the same exportSheets hook the executive
  // summary uses for its per-year tabs; one mechanism, so the two cannot drift.
  exportSheets({ rows, columns, totals, footnotes }) {
    const groups = byYear(rows);
    const total = groups.reduce((s, g) => ({
      count: s.count + g.count,
      value: g.value == null ? s.value : (s.value ?? 0) + g.value,
    }), { count: 0, value: null });
    return [
      {
        name: 'Summary',
        columns: [
          { key: 'year', label: 'Tax Year', type: 'year' },
          { key: 'count', label: 'Open Appeals', type: 'int' },
          { key: 'value', label: 'Value Under Appeal', type: 'value' },
        ],
        rows: groups,
        totals: { year: 'TOTAL', count: total.count, value: total.value },
      },
      { name: 'Detail', columns, rows, totals: totals || undefined, footnotes },
    ];
  },

  emptyMessage:
    'No outstanding appeals. Statuses other than Appeal Open and Under Review are excluded, and so are '
    + 'parcel-years with no appeal status at all — rolled years are written without one, and a year with no '
    + 'notice is not an open appeal.',
};

export default outstandingAppeals;
